import React from "react";
import { Space, Stack, Text, Group, Select, Center } from "@mantine/core";
import { FormList } from "@mantine/form";
import { UseFormReturnType } from "@mantine/form/lib/use-form";
import { v4 as uuidv4 } from "uuid";
import RuleCondition from "./RuleCondition";
import AvailableField from "../services/model/AvailableField";
import SegmentRule from "../services/model/SegmentRule";

type RuleBuilderProps = {
  form: UseFormReturnType<{
    name: string;
    description: string;
    rules: FormList<SegmentRule>;
    logic_operator: "AND" | "OR";
    is_active: boolean;
  }>;
  availableFields: AvailableField[];
};

const RuleBuilder: React.FC<RuleBuilderProps> = ({ form, availableFields }) => {
  const addRule = (): void => {
    form.addListItem("rules", {
      id: uuidv4(),
      field: "",
      operator: "",
      value: "",
    });
  };

  const removeRule = (index: number): void => {
    form.removeListItem("rules", index);
  };

  const handleLogicOperatorChange = (value: string | null): void => {
    if (value === "AND" || value === "OR") {
      form.setFieldValue("logic_operator", value);
    }
  };

  return (
    <div>
      <Group spacing="xs" mb="md">
        <Text weight={500} size="sm">
          Match
        </Text>
        <Select
          data={[
            { value: "ANY", label: "ANY" },
            { value: "ALL", label: "ALL" },
          ]}
          value={form.values.logic_operator === "AND" ? "ALL" : "ANY"}
          onChange={(value) =>
            handleLogicOperatorChange(value === "ALL" ? "AND" : "OR")
          }
          size="xs"
          style={{ width: 80 }}
        />
        <Text weight={500} size="sm">
          of the following rules
        </Text>
      </Group>
      <Stack spacing="sm">
        {form.values.rules.map((rule, index) => (
          <React.Fragment key={rule.id || index}>
            <RuleCondition
              index={index}
              form={form}
              availableFields={availableFields}
              onRemove={removeRule}
              onAdd={addRule}
              canRemove={form.values.rules.length > 1}
            />
            {index < form.values.rules.length - 1 && (
              <Center>
                <Text
                  size="sm"
                  weight={600}
                  color="dimmed"
                  style={{ padding: "8px 0" }}
                >
                  {form.values.logic_operator === "AND" ? "AND" : "OR"}
                </Text>
              </Center>
            )}
          </React.Fragment>
        ))}
      </Stack>
      {form.errors.rules && typeof form.errors.rules === "string" && (
        <>
          <Space h="xs" />
          <Text color="red" size="sm">
            {form.errors.rules}
          </Text>
        </>
      )}
    </div>
  );
};

export default RuleBuilder;
